/**
 * CmsLayoutDetailPage — Layout Editor
 *
 * Edit a named layout: content width, header/nav/footer style,
 * which nav menus to use, and whether this is the default layout.
 */

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, LayoutTemplate, Star } from 'lucide-react';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { PageHeader } from '../components/ui/PageHeader.tsx';
import { Button } from '../components/ui/Button.tsx';
import { Input, Select } from '../components/ui/Input.tsx';
import { useToast } from '../components/ui/Toast.tsx';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface CmsLayout {
  id: string;
  name: string;
  slug: string;
  config: {
    contentWidth?: string;
    headerStyle?: string;
    navPosition?: string;
    footerStyle?: string;
  };
  isDefault: boolean;
  headerMenuSlug: string | null;
  footerMenuSlug: string | null;
  createdAt: string;
  updatedAt: string;
}

interface NavMenu {
  id: string;
  name: string;
  slug: string;
}

interface LayoutForm {
  name: string;
  contentWidth: string;
  headerStyle: string;
  navPosition: string;
  footerStyle: string;
  headerMenuSlug: string;
  footerMenuSlug: string;
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export function CmsLayoutDetailPage() {
  const { spaceId, layoutId } = useParams<{ spaceId: string; layoutId: string }>();
  const qc = useQueryClient();
  const toast = useToast();

  const BASE = `/api/v1/cms/spaces/${spaceId}/layouts`;

  const [form, setForm] = useState<LayoutForm>({
    name: '',
    contentWidth: 'normal',
    headerStyle: 'sticky',
    navPosition: 'top',
    footerStyle: 'minimal',
    headerMenuSlug: '',
    footerMenuSlug: '',
  });
  const [isDirty, setIsDirty] = useState(false);

  // ── Queries ─────────────────────────────────────────────────────────────────

  const { data: layout, isLoading } = useQuery<CmsLayout>({
    queryKey: ['cms-layout', spaceId, layoutId],
    queryFn: () => api.get(`${BASE}/${layoutId}`).then((r) => r.data),
    enabled: !!spaceId && !!layoutId,
  });

  const { data: menus = [] } = useQuery<NavMenu[]>({
    queryKey: ['cms-nav-menus', spaceId],
    queryFn: () =>
      api.get(`/api/v1/cms/spaces/${spaceId}/nav/menus`).then((r) => r.data),
    enabled: !!spaceId,
  });

  useEffect(() => {
    if (!layout) return;
    setForm({
      name: layout.name,
      contentWidth: (layout.config.contentWidth as string) || 'normal',
      headerStyle: (layout.config.headerStyle as string) || 'sticky',
      navPosition: (layout.config.navPosition as string) || 'top',
      footerStyle: (layout.config.footerStyle as string) || 'minimal',
      headerMenuSlug: layout.headerMenuSlug ?? '',
      footerMenuSlug: layout.footerMenuSlug ?? '',
    });
    setIsDirty(false);
  }, [layout]);

  // ── Mutations ────────────────────────────────────────────────────────────────

  const saveMutation = useMutation({
    mutationFn: () =>
      api.patch(`${BASE}/${layoutId}`, {
        name: form.name.trim(),
        config: {
          contentWidth: form.contentWidth,
          headerStyle: form.headerStyle,
          navPosition: form.navPosition,
          footerStyle: form.footerStyle,
        },
        headerMenuSlug: form.headerMenuSlug || null,
        footerMenuSlug: form.footerMenuSlug || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cms-layout', spaceId, layoutId] });
      qc.invalidateQueries({ queryKey: ['cms-layouts', spaceId] });
      setIsDirty(false);
      toast.success('Layout saved');
    },
    onError: (err) => toast.error(extractError(err, 'Failed to save layout')),
  });

  const setDefaultMutation = useMutation({
    mutationFn: () => api.post(`${BASE}/${layoutId}/set-default`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cms-layout', spaceId, layoutId] });
      qc.invalidateQueries({ queryKey: ['cms-layouts', spaceId] });
      toast.success('Set as default layout');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  // ── Handlers ─────────────────────────────────────────────────────────────────

  function setField<K extends keyof LayoutForm>(key: K, value: LayoutForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    setIsDirty(true);
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-64 bg-zinc-100 rounded animate-pulse" />
        <div className="h-64 bg-zinc-100 rounded-xl animate-pulse" />
      </div>
    );
  }

  if (!layout) {
    return (
      <div className="text-center py-16 text-zinc-400">
        <p className="text-sm">Layout not found.</p>
        <Link to={`/spaces/${spaceId}/layouts`} className="text-xs underline mt-2 inline-block">
          Back to layouts
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-xs text-zinc-400">
        <Link
          to={`/spaces/${spaceId}/layouts`}
          className="hover:text-zinc-600 flex items-center gap-1"
        >
          <ChevronLeft size={13} />
          Layouts
        </Link>
        <span>/</span>
        <span className="text-zinc-700 font-medium">{layout.name}</span>
      </div>

      <PageHeader
        title={layout.name}
        description={`Configure the layout structure — content width, header, navigation and footer style.`}
        action={
          <div className="flex items-center gap-2">
            {!layout.isDefault && (
              <Button
                variant="ghost"
                size="sm"
                loading={setDefaultMutation.isPending}
                onClick={() => setDefaultMutation.mutate()}
              >
                <Star size={13} className="mr-1.5" /> Set as Default
              </Button>
            )}
            <Button
              size="sm"
              disabled={!isDirty}
              loading={saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
            >
              Save Changes
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Identity */}
        <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-4">
          <div className="flex items-center gap-2 text-sm font-medium text-zinc-700">
            <LayoutTemplate size={15} /> Identity
          </div>
          <Input
            label="Name"
            value={form.name}
            onChange={(e) => setField('name', e.target.value)}
          />
          <div>
            <label className="block text-xs font-medium text-zinc-600 mb-1">Slug</label>
            <div className="px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-xs font-mono text-zinc-500">
              {layout.slug}
            </div>
          </div>
          {layout.isDefault && (
            <div className="flex items-center gap-1.5 text-xs text-emerald-600 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
              <Star size={12} />
              This is the default layout for this space
            </div>
          )}
        </div>

        {/* Content */}
        <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-4">
          <div className="text-sm font-medium text-zinc-700">Content</div>
          <Select
            label="Content Width"
            value={form.contentWidth}
            onChange={(e) => setField('contentWidth', e.target.value)}
          >
            <option value="narrow">Narrow (720px)</option>
            <option value="normal">Normal (1024px)</option>
            <option value="wide">Wide (1280px)</option>
            <option value="full">Full Width</option>
          </Select>
        </div>

        {/* Header & Nav */}
        <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-4">
          <div className="text-sm font-medium text-zinc-700">Header & Navigation</div>
          <Select
            label="Header Style"
            value={form.headerStyle}
            onChange={(e) => setField('headerStyle', e.target.value)}
          >
            <option value="sticky">Sticky</option>
            <option value="static">Static</option>
            <option value="transparent">Transparent</option>
            <option value="minimal">Minimal</option>
          </Select>
          <Select
            label="Navigation Position"
            value={form.navPosition}
            onChange={(e) => setField('navPosition', e.target.value)}
          >
            <option value="top">Top (horizontal)</option>
            <option value="sidebar-left">Sidebar Left</option>
          </Select>
          <Select
            label="Header Menu"
            value={form.headerMenuSlug}
            onChange={(e) => setField('headerMenuSlug', e.target.value)}
          >
            <option value="">— None —</option>
            {menus.map((m) => (
              <option key={m.id} value={m.slug}>
                {m.name} ({m.slug})
              </option>
            ))}
          </Select>
        </div>

        {/* Footer */}
        <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-4">
          <div className="text-sm font-medium text-zinc-700">Footer</div>
          <Select
            label="Footer Style"
            value={form.footerStyle}
            onChange={(e) => setField('footerStyle', e.target.value)}
          >
            <option value="none">None</option>
            <option value="minimal">Minimal</option>
            <option value="full">Full</option>
          </Select>
          <Select
            label="Footer Menu"
            value={form.footerMenuSlug}
            onChange={(e) => setField('footerMenuSlug', e.target.value)}
          >
            <option value="">— None —</option>
            {menus.map((m) => (
              <option key={m.id} value={m.slug}>
                {m.name} ({m.slug})
              </option>
            ))}
          </Select>
        </div>
      </div>

      {saveMutation.isError && (
        <p className="text-xs text-red-500">{extractError(saveMutation.error)}</p>
      )}
    </div>
  );
}
